import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction, toText } from '@yuan-math/math';
import {
  choiceExercise,
  expressionExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Series, induction, counting, the binomial theorem and probability
 * distributions.
 *
 * Counting, the binomial theorem and probability distributions share their
 * arithmetic on purpose, and so do these
 * templates: `choose` below is the same function behind a binomial coefficient
 * and behind the probability of k heads in n tosses, because those are the same
 * number counted for the same reason. Duplicating it per file would have hidden
 * the one fact this half of the course exists to make.
 *
 * Two items are built backwards from the wrong answer. `binomial-coefficient`
 * refuses k = 0 and k = n, where the coefficient is genuinely 1 and a student
 * who omits coefficients altogether would be marked correct. `expected-value`
 * forces the probabilities apart, because on a uniform distribution the
 * unweighted mean is the right answer and the item would diagnose nothing.
 *
 * The induction items are the odd ones out, and deliberately so. Nothing in
 * that concept has a number for an answer — a proof is right or wrong for
 * reasons, not by arithmetic — so the templates ask for the two things that
 * *can* be checked mechanically and are exactly where students go wrong:
 * stating P(k + 1) by substituting, and naming the term the step adds. The
 * third, `induction-flaw`, is multiple choice because "what is missing from
 * this proof" has no numerical answer at all, and the student's model of what
 * a proof is is the whole content of the item.
 */

/** n choose k, exactly, without going through a factorial that overflows. */
function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let result = 1;
  for (let i = 1; i <= Math.min(k, n - k); i += 1) {
    result = (result * (n - Math.min(k, n - k) + i)) / i;
  }
  return Math.round(result);
}

/**
 * A power as it is written by hand: `x`, not `x^1`.
 *
 * The prompt of `binomial-coefficient` names the term it is asking about, and
 * the outermost terms of every expansion have a first power in them. Printing
 * `x^1y^3` tells a student that a notation exists which nobody uses.
 */
const power = (base: string, exponent: number): string =>
  exponent === 1 ? base : `${base}^${exponent}`;

/** n permute r — arrangements of r items chosen from n. */
function permute(n: number, r: number): number {
  let result = 1;
  for (let i = 0; i < r; i += 1) result *= n - i;
  return result;
}

/**
 * Drops any trap that repeats the answer or an earlier trap.
 *
 * Two traps on one exercise that normalise to the same response are
 * indistinguishable: whichever is listed first wins, and the student is told
 * about an error they may not have made. Generated parameters make that easy to
 * author by accident — the induction claims below have a family where the 3rd
 * term and the sum of the first 2 terms are both 3.
 */
const distinctTraps = (answer: number, traps: Trap[]): Trap[] => {
  const seen = new Set<string>([String(answer)]);
  return traps.filter((entry) => {
    if (seen.has(entry.response)) return false;
    seen.add(entry.response);
    return true;
  });
};

/**
 * The summation claims the induction items are built on.
 *
 * Each carries four views of the same statement, because the templates ask
 * about all four: the total up to n, the nth term of the left-hand side, the
 * term the step adds, and the right-hand side with any variable substituted
 * in. Written as functions of a *variable name* rather than of `n`, so that
 * "state P(k + 1)" is a substitution rather than a second hand-written claim
 * that could disagree with the first.
 */
/**
 * An expression in two forms: the one the grader parses and the one a student
 * reads.
 *
 * They cannot be the same string. `2*(k) + 1` is what the expression parser
 * wants, brackets and all, and `2k + 1` is what anybody writes — and a step in
 * a worked solution printed in the first form teaches a notation that does not
 * exist.
 */
type Form = { expr: string; latex: string };

type Claim = {
  /** The claim as it is displayed. */
  latex: string;
  /** The claim in a sentence, for a prompt that has already opened one. */
  words: string;
  /** The total of the first n terms. */
  sum: (n: number) => number;
  /** The nth term of the left-hand side, as a number. */
  term: (n: number) => number;
  /** That term in a variable, and the one the step from P(v) to P(v + 1) adds. */
  termAt: (v: string) => Form;
  nextTerm: (v: string) => Form;
  /** The right-hand side at n = v, and at n = v + 1. */
  rhsAt: (v: string) => Form;
  rhsNext: (v: string) => Form;
};

const CLAIMS: readonly Claim[] = [
  {
    latex: '1 + 2 + 3 + \\cdots + n = \\frac{n(n + 1)}{2}',
    words: 'the whole numbers from 1 to n add to n(n + 1)/2',
    sum: (n) => (n * (n + 1)) / 2,
    term: (n) => n,
    termAt: (v) => ({ expr: `(${v})`, latex: `${v}` }),
    nextTerm: (v) => ({ expr: `(${v}) + 1`, latex: `${v} + 1` }),
    rhsAt: (v) => ({ expr: `(${v})*((${v}) + 1)/2`, latex: `\\frac{${v}(${v} + 1)}{2}` }),
    rhsNext: (v) => ({ expr: `((${v}) + 1)*((${v}) + 2)/2`, latex: `\\frac{(${v} + 1)(${v} + 2)}{2}` }),
  },
  {
    latex: '1 + 3 + 5 + \\cdots + (2n - 1) = n^{2}',
    words: 'the first n odd numbers add to n²',
    sum: (n) => n * n,
    term: (n) => 2 * n - 1,
    termAt: (v) => ({ expr: `2*(${v}) - 1`, latex: `2${v} - 1` }),
    nextTerm: (v) => ({ expr: `2*(${v}) + 1`, latex: `2${v} + 1` }),
    rhsAt: (v) => ({ expr: `(${v})^2`, latex: `${v}^{2}` }),
    rhsNext: (v) => ({ expr: `((${v}) + 1)^2`, latex: `(${v} + 1)^{2}` }),
  },
  {
    latex: '2 + 4 + 6 + \\cdots + 2n = n(n + 1)',
    words: 'the first n even numbers add to n(n + 1)',
    sum: (n) => n * (n + 1),
    term: (n) => 2 * n,
    termAt: (v) => ({ expr: `2*(${v})`, latex: `2${v}` }),
    nextTerm: (v) => ({ expr: `2*(${v}) + 2`, latex: `2${v} + 2` }),
    rhsAt: (v) => ({ expr: `(${v})*((${v}) + 1)`, latex: `${v}(${v} + 1)` }),
    rhsNext: (v) => ({ expr: `((${v}) + 1)*((${v}) + 2)`, latex: `(${v} + 1)(${v} + 2)` }),
  },
  {
    latex: '1 + 2 + 4 + \\cdots + 2^{n-1} = 2^{n} - 1',
    words: 'the powers of 2 from 1 up to 2ⁿ⁻¹ add to 2ⁿ − 1',
    sum: (n) => 2 ** n - 1,
    term: (n) => 2 ** (n - 1),
    termAt: (v) => ({ expr: `2^((${v}) - 1)`, latex: `2^{${v} - 1}` }),
    nextTerm: (v) => ({ expr: `2^(${v})`, latex: `2^{${v}}` }),
    rhsAt: (v) => ({ expr: `2^(${v}) - 1`, latex: `2^{${v}} - 1` }),
    rhsNext: (v) => ({ expr: `2^((${v}) + 1) - 1`, latex: `2^{${v} + 1} - 1` }),
  },
  {
    latex: '1^{2} + 2^{2} + \\cdots + n^{2} = \\frac{n(n + 1)(2n + 1)}{6}',
    words: 'the squares from 1² to n² add to n(n + 1)(2n + 1)/6',
    sum: (n) => (n * (n + 1) * (2 * n + 1)) / 6,
    term: (n) => n * n,
    termAt: (v) => ({ expr: `(${v})^2`, latex: `${v}^{2}` }),
    nextTerm: (v) => ({ expr: `((${v}) + 1)^2`, latex: `(${v} + 1)^{2}` }),
    rhsAt: (v) => ({
      expr: `(${v})*((${v}) + 1)*(2*(${v}) + 1)/6`,
      latex: `\\frac{${v}(${v} + 1)(2${v} + 1)}{6}`,
    }),
    rhsNext: (v) => ({
      expr: `((${v}) + 1)*((${v}) + 2)*(2*(${v}) + 3)/6`,
      latex: `\\frac{(${v} + 1)(${v} + 2)(2${v} + 3)}{6}`,
    }),
  },
];

/** The variable a "state the next case" item is written in. */
const LETTERS = ['k', 'm', 'r'] as const;

export const COUNTING_TEMPLATES: ExerciseTemplate[] = [
  /* ----------------------------------------------------------------- series */
  template({
    id: 'arithmetic-series-sum',
    conceptId: 'series',
    description: 'Sum the first n terms of an arithmetic series.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const first = rng.int(2, 12);
      const difference = rng.int(2, difficulty === 3 ? 9 : 5);
      const n = rng.int(6, difficulty === 3 ? 20 : 12);
      const last = first + (n - 1) * difference;
      const sum = (n * (first + last)) / 2;

      return numericExercise({
        prompt: `An arithmetic series starts ${first}, ${first + difference}, ${first + 2 * difference}, … What is the sum of its first ${n} terms?`,
        value: sum,
        summary: `The ${n}th term is ${first} + ${n - 1}(${difference}) = ${last}, and the sum is ${n} × the average of the ends: (${n}/2)(${first} + ${last}) = ${sum}.`,
        hint: 'Pair the first term with the last, as Gauss did. What is each pair worth?',
        steps: [
          { label: 'Find the last term.', latex: `a_{${n}} = ${first} + ${n - 1}(${difference}) = ${last}` },
          { label: 'Average the ends and multiply by the count.', latex: `S_{${n}} = \\frac{${n}}{2}(${first} + ${last})` },
          { label: 'Evaluate.', latex: `${sum}` },
        ],
        // The last term reported as the total.
        traps: [trap(last, 'term-given-for-sum')],
      });
    },
  }),

  template({
    id: 'arithmetic-series-partial',
    conceptId: 'series',
    description: 'Sum a run of terms that does not start at the first.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const first = rng.int(1, 9);
      const difference = rng.int(2, 7);
      const from = rng.int(3, 8);
      const to = from + rng.int(3, 9);
      const count = to - from + 1;
      const termAt = (index: number): number => first + (index - 1) * difference;
      const sum = (count * (termAt(from) + termAt(to))) / 2;

      return numericExercise({
        prompt: `A sequence has nth term ${first} + ${difference}(n − 1). What is the sum of the ${from}th to the ${to}th terms?`,
        value: sum,
        summary: `From the ${from}th to the ${to}th there are ${to} − ${from} + 1 = ${count} terms, running from ${termAt(from)} to ${termAt(to)}. Their sum is (${count}/2)(${termAt(from)} + ${termAt(to)}) = ${sum}.`,
        hint: 'Count the terms. Both ends are included, so it is not simply the difference of the indices.',
        steps: [
          { label: 'Count the terms.', latex: `${to} - ${from} + 1 = ${count}` },
          { label: 'Find the two end terms.', latex: `${termAt(from)} \\text{ and } ${termAt(to)}` },
          { label: 'Average the ends and multiply.', latex: `\\frac{${count}}{2}(${termAt(from)} + ${termAt(to)}) = ${sum}` },
        ],
        // One term short: the indices subtracted without adding the 1 back.
        traps: [trap(((count - 1) * (termAt(from) + termAt(to - 1))) / 2, 'series-term-count-off-by-one')],
      });
    },
  }),

  template({
    id: 'geometric-ratio',
    conceptId: 'series',
    description: 'Find the common ratio of a geometric sequence.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const first = rng.int(2, 9);
      const ratio = rng.pick([2, 3, 4, 5]);

      return numericExercise({
        prompt: `What is the common ratio of the geometric sequence ${first}, ${first * ratio}, ${first * ratio * ratio}, ${first * ratio ** 3}, …?`,
        value: ratio,
        summary: `Divide one term by the one before it: ${first * ratio} ÷ ${first} = ${ratio}. A geometric sequence multiplies, so the ratio is found by dividing.`,
        hint: 'Geometric means multiplying. What must each term be multiplied by?',
        steps: [
          { label: 'Divide consecutive terms.', latex: `\\frac{${first * ratio}}{${first}} = ${ratio}` },
          { label: 'Check it on the next pair.', latex: `\\frac{${first * ratio * ratio}}{${first * ratio}} = ${ratio}` },
        ],
        // Subtracting, which is how an arithmetic sequence's difference is found.
        traps: [...(first * (ratio - 1) === ratio ? [] : [trap(first * (ratio - 1), 'geometric-ratio-as-difference')])],
      });
    },
  }),

  template({
    id: 'infinite-geometric-sum',
    conceptId: 'series',
    description: 'Total an infinite geometric series.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const first = rng.int(2, 15);
      const denominator = rng.int(2, 6);

      return fractionExercise({
        prompt: `An infinite geometric series has first term ${first} and common ratio 1/${denominator}. What is its total?`,
        latex: `${first} + \\frac{${first}}{${denominator}} + \\frac{${first}}{${denominator * denominator}} + \\cdots`,
        value: fraction(first * denominator, denominator - 1),
        requireSimplified: true,
        summary: `Since |r| < 1 the total is a₁ ÷ (1 − r) = ${first} ÷ (1 − 1/${denominator}) = ${toText(fraction(first * denominator, denominator - 1))}.`,
        hint: 'The terms shrink, so there is a total. Divide the first term by 1 − r.',
        steps: [
          { label: 'Check that the terms shrink.', latex: `\\left|\\tfrac{1}{${denominator}}\\right| < 1` },
          { label: 'Apply the formula.', latex: `\\frac{${first}}{1 - \\frac{1}{${denominator}}}` },
          { label: 'Evaluate.', latex: `${toText(fraction(first * denominator, denominator - 1))}` },
        ],
        // The first term offered as the whole total.
        traps: [fractionTrap(fraction(first), 'term-given-for-sum')],
      });
    },
  }),

  template({
    id: 'infinite-series-exists',
    conceptId: 'series',
    description: 'Decide whether an endless sum has a total at all.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const converges = rng.bool();
      const first = rng.int(2, 12);
      const denominator = rng.int(2, 5);
      const growth = rng.int(2, 4);
      const total = first * denominator - first;

      return choiceExercise({
        prompt: 'Does this endless sum have a finite total?',
        latex: converges
          ? `${first} + \\frac{${first}}{${denominator}} + \\frac{${first}}{${denominator * denominator}} + \\cdots`
          : `${first} + ${first * growth} + ${first * growth * growth} + \\cdots`,
        ...options(
          converges
            ? [
                { label: `Yes — it totals ${toText(fraction(first * denominator, denominator - 1))}`, correct: true },
                { label: 'No — it has infinitely many terms', misconceptionId: 'endless-sum-called-endless' },
                { label: `Yes — it totals ${first}`, misconceptionId: 'term-given-for-sum' },
                { label: `Yes — it totals ${total === 0 ? first + 1 : total}` },
              ]
            : [
                { label: 'No — the terms grow, so the total grows without bound', correct: true },
                { label: `Yes — it totals ${first * growth}`, misconceptionId: 'term-given-for-sum' },
                { label: `Yes — it totals ${first * growth * growth}` },
                { label: 'No — no endless sum ever has a total', misconceptionId: 'endless-sum-called-endless' },
              ],
          rng.shuffle,
        ),
        summary: converges
          ? `Each term is 1/${denominator} of the one before, so |r| < 1 and the running total closes in on ${toText(fraction(first * denominator, denominator - 1))}.`
          : `Each term is ${growth} times the one before, so the terms never shrink and the running total passes every number you name. There is no total.`,
        hint: 'Do the terms shrink towards nothing, or not? That is the only question.',
      });
    },
  }),

  /* --------------------------------------------------------------- induction */
  template({
    id: 'induction-base-case-check',
    conceptId: 'mathematical-induction',
    description: 'Evaluate the base case of a summation claim.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const claim = rng.pick(CLAIMS);
      const n = rng.int(2, difficulty === 3 ? 6 : 4);
      const total = claim.sum(n);
      const written = Array.from({ length: n }, (_, i) => claim.term(i + 1)).join(' + ');

      return numericExercise({
        prompt: `The claim P(n) says that ${claim.words}. Checking it at n = ${n}, what is the total on the left-hand side?`,
        latex: claim.latex,
        value: total,
        summary: `The first ${n} terms are ${written}, which come to ${total} — and that is what the right-hand side gives at n = ${n}, so P(${n}) holds.`,
        hint: 'Write out the first few terms of the left-hand side and add them. The last one is not the answer.',
        steps: [
          { label: `Write out the first ${n} terms.`, latex: written },
          { label: 'Add them.', latex: `${total}` },
        ],
        traps: distinctTraps(total, [
          // The last term reported instead of the running total.
          trap(claim.term(n), 'term-given-for-sum'),
          // One term short: the claim checked at n − 1 by mistake.
          trap(claim.sum(n - 1), 'series-term-count-off-by-one'),
        ]),
      });
    },
  }),

  template({
    id: 'induction-new-term',
    conceptId: 'mathematical-induction',
    description: 'Name the term the inductive step adds to the left-hand side.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const claim = rng.pick(CLAIMS);
      const v = rng.pick(LETTERS);
      const next = claim.nextTerm(v);

      return expressionExercise({
        prompt: `P(n) claims that ${claim.words}. Going from P(${v}) to P(${v} + 1), what single term is added to the left-hand side? Write it in terms of ${v}.`,
        latex: claim.latex,
        canonical: next.expr,
        summary: `The left-hand side of P(${v} + 1) is the left-hand side of P(${v}) with one more term on the end: the (${v} + 1)th term, which is ${next.latex}.`,
        hint: `Which term is the (${v} + 1)th — not the ${v}th?`,
        steps: [
          { label: 'Write the nth term of the left-hand side.', latex: claim.termAt('n').latex },
          { label: `Substitute n = ${v} + 1.`, latex: next.latex },
        ],
        // The vth term rather than the (v + 1)th — one place short.
        traps: [trap(claim.termAt(v).expr, 'series-term-count-off-by-one')],
      });
    },
  }),

  template({
    id: 'induction-step-target',
    conceptId: 'mathematical-induction',
    description: 'State the right-hand side of P(k + 1) by substituting.',
    difficulties: [3, 4, 5],
    build: ({ rng }) => {
      const claim = rng.pick(CLAIMS);
      const v = rng.pick(LETTERS);
      const target = claim.rhsNext(v);

      return expressionExercise({
        prompt: `P(n) claims that ${claim.words}. Write the right-hand side of P(${v} + 1), in terms of ${v}.`,
        latex: claim.latex,
        canonical: target.expr,
        summary: `P(${v} + 1) is the same claim with ${v} + 1 written everywhere n was, so the right-hand side becomes ${target.latex}.`,
        hint: `Replace every n on the right-hand side by ${v} + 1 — every one of them, then tidy up.`,
        steps: [
          { label: 'Start from the right-hand side.', latex: claim.rhsAt('n').latex },
          { label: `Substitute n = ${v} + 1 everywhere.`, latex: claim.rhsNext(v).latex },
        ],
        // The target written with the variable unchanged — which is P(v) again.
        traps: [trap(claim.rhsAt(v).expr, 'induction-step-not-substituted')],
      });
    },
  }),

  template({
    id: 'induction-flaw',
    conceptId: 'mathematical-induction',
    description: 'Find what a broken induction proof is missing.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const scenarios = [
        {
          prompt:
            'A student shows that if the claim holds at n = k then it holds at n = k + 1, and stops there. What is missing?',
          correct: 'A base case — a check that the claim holds at n = 1',
          summary:
            'The step only carries a claim forward from somewhere. "If it holds at k then it holds at k + 1" is true of the false claim n = n + 1 as well, so without a case that is actually true, the step establishes nothing.',
          hint: 'Both dominoes facts are needed. Which one has been shown?',
          wrong: [
            { label: 'Nothing — the step is the whole of an induction proof', misconceptionId: 'induction-base-case-omitted' },
            { label: 'The claim should be assumed for every n, not for a single k', misconceptionId: 'induction-conclusion-assumed' },
            { label: 'A few more values of n should be checked, to be sure', misconceptionId: 'pattern-checked-not-proved' },
          ],
        },
        {
          prompt:
            'A student checks the claim at n = 1, 2, 3, 4 and 5, finds it true every time, and concludes that it holds for every n. What is missing?',
          correct: 'An inductive step — an argument carrying the claim from n = k to n = k + 1',
          summary:
            'Five cases are five cases. n² + n + 41 is prime for every n from 0 to 39 and composite at 40, so no run of successes settles a claim about every whole number; only the general step does.',
          hint: 'How many values of n does the argument actually cover?',
          wrong: [
            { label: 'Nothing — five cases in a row settle it', misconceptionId: 'pattern-checked-not-proved' },
            { label: 'A base case, which has not been checked yet', misconceptionId: 'induction-base-case-omitted' },
            { label: 'The claim should be assumed for every n first', misconceptionId: 'induction-conclusion-assumed' },
          ],
        },
        {
          prompt:
            'A student writes "assume the claim is true for every whole number n", and then verifies it at n = k + 1. What is wrong?',
          correct: 'The claim may be assumed for one value only — assuming it for every n assumes the conclusion',
          summary:
            'The inductive hypothesis is P(k) for a single k, and the step must earn P(k + 1) from it. Assuming P(n) for every n has already assumed what the proof set out to establish.',
          hint: 'What exactly is the proof trying to establish, and what has it assumed?',
          wrong: [
            { label: 'Nothing — that is what the inductive hypothesis says', misconceptionId: 'induction-conclusion-assumed' },
            { label: 'Nothing is wrong, but more cases should be checked first', misconceptionId: 'pattern-checked-not-proved' },
            { label: 'The step should run from n = k + 1 back to n = k' },
          ],
        },
      ];
      const scenario = rng.pick(scenarios);

      return choiceExercise({
        prompt: scenario.prompt,
        ...options([{ label: scenario.correct, correct: true }, ...scenario.wrong], rng.shuffle),
        summary: scenario.summary,
        hint: scenario.hint,
      });
    },
  }),

  /* ---------------------------------------------------------------- counting */
  template({
    id: 'multiplication-principle',
    conceptId: 'combinatorics-intro',
    description: 'Count a choice made in independent stages.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const stages =
        difficulty === 3
          ? [
              { count: rng.int(3, 7), name: 'starters' },
              { count: rng.int(3, 7), name: 'main courses' },
              { count: rng.int(2, 5), name: 'puddings' },
            ]
          : [
              { count: rng.int(3, 9), name: 'shirts' },
              { count: rng.int(2, 8), name: 'pairs of trousers' },
            ];
      const product = stages.reduce((total, stage) => total * stage.count, 1);
      const sum = stages.reduce((total, stage) => total + stage.count, 0);
      const listed = stages.map((stage) => `${stage.count} ${stage.name}`).join(', ');
      const setting = stages.length === 3 ? 'A set menu offers' : 'A wardrobe holds';

      return numericExercise({
        prompt: `${setting} ${listed}. How many different combinations are there, taking one of each?`,
        value: product,
        summary: `Each choice can be made alongside every other, so the counts multiply: ${stages.map((stage) => stage.count).join(' × ')} = ${product}.`,
        hint: 'Every option at one stage can go with every option at the next.',
        steps: [
          { label: 'Multiply the stages.', latex: stages.map((stage) => stage.count).join(' \\times ') },
          { label: 'Evaluate.', latex: `${product}` },
        ],
        // Adding the stages, which counts the options rather than the outcomes.
        traps: [...(sum === product ? [] : [trap(sum, 'counting-stages-added')])],
      });
    },
  }),

  template({
    id: 'combination-value',
    conceptId: 'combinatorics-intro',
    description: 'Count selections where order does not matter.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const n = rng.int(difficulty === 4 ? 9 : 6, difficulty === 4 ? 16 : 10);
      const r = rng.int(2, Math.min(4, n - 2));
      const answer = choose(n, r);
      const arrangements = permute(n, r);

      return numericExercise({
        prompt: `A class of ${n} must send ${r} students to a conference. In how many ways can the group be chosen?`,
        value: answer,
        summary: `Order does not matter — a group of ${r} is a group of ${r} — so it is a combination: ${n}C${r} = ${answer}.`,
        hint: 'Would swapping two of the chosen students give a different outcome?',
        steps: [
          { label: 'Order does not matter, so use a combination.', latex: `{}^{${n}}C_{${r}}` },
          { label: 'Write it out and cancel.', latex: `\\frac{${n}!}{${r}!\\,${n - r}!}` },
          { label: 'Evaluate.', latex: `${answer}` },
        ],
        // Counting arrangements: the answer is r! times too large.
        traps: [trap(arrangements, 'order-counted-when-it-does-not-matter')],
      });
    },
  }),

  template({
    id: 'permutation-value',
    conceptId: 'combinatorics-intro',
    description: 'Count arrangements where order matters.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const n = rng.int(difficulty === 4 ? 8 : 5, difficulty === 4 ? 14 : 9);
      const r = rng.int(2, Math.min(4, n - 2));
      const answer = permute(n, r);
      const selections = choose(n, r);
      const description =
        r === 2 ? 'first and second place' : r === 3 ? 'gold, silver and bronze' : `the first ${r} places`;

      return numericExercise({
        prompt: `${n} runners compete for ${description}. In how many ways can those places be filled?`,
        value: answer,
        summary: `Each place is a different job, so the same ${r} runners in another order is a different result: ${n}P${r} = ${answer}.`,
        hint: 'Does swapping two of the chosen runners change the outcome?',
        steps: [
          { label: 'Order matters, so use a permutation.', latex: `{}^{${n}}P_{${r}}` },
          { label: 'Multiply the choices down.', latex: Array.from({ length: r }, (_, i) => n - i).join(' \\times ') },
          { label: 'Evaluate.', latex: `${answer}` },
        ],
        // Counting selections, which throws away the orderings the question wants.
        traps: [...(selections === answer ? [] : [trap(selections, 'order-ignored-when-it-matters')])],
      });
    },
  }),

  template({
    id: 'permutation-or-combination',
    conceptId: 'combinatorics-intro',
    description: 'Decide whether order matters before counting.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const cases = [
        { question: 'choosing 3 books from 10 to take on holiday', ordered: false, why: 'The three books are a set — reading them in another order is the same choice.' },
        { question: 'choosing a 4-digit PIN from the digits 0–9', ordered: true, why: '1234 and 4321 are different PINs, so the order is part of the answer.' },
        { question: 'awarding gold, silver and bronze to 3 of 8 runners', ordered: true, why: 'The three medals are different jobs, so who gets which one matters.' },
        { question: 'picking 5 cards from a pack of 52', ordered: false, why: 'A hand is a hand however it was dealt.' },
        { question: 'seating 6 people in a row of 6 chairs', ordered: true, why: 'Two people swapping chairs gives a different seating.' },
        { question: 'choosing a committee of 4 from 12 members', ordered: false, why: 'A committee is a set of people, with no positions attached.' },
      ];
      const chosen = rng.pick(cases);

      return choiceExercise({
        prompt: `You are counting the ways of ${chosen.question}. Which should you use?`,
        ...options(
          [
            { label: chosen.ordered ? 'A permutation' : 'A combination', correct: true },
            {
              label: chosen.ordered ? 'A combination' : 'A permutation',
              misconceptionId: chosen.ordered ? 'order-ignored-when-it-matters' : 'order-counted-when-it-does-not-matter',
            },
            { label: 'Add the two counts together' },
            { label: 'Neither — list them out' },
          ],
          rng.shuffle,
        ),
        summary: chosen.why,
        hint: 'Swap two of the chosen items over. Are you looking at the same outcome?',
      });
    },
  }),

  /* ------------------------------------------------------- binomial theorem */
  template({
    id: 'binomial-coefficient',
    conceptId: 'binomial-theorem',
    description: 'Find a binomial coefficient, and see it as a count.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const n = rng.int(4, difficulty === 3 ? 12 : 7);
      /* k = 0 and k = n are excluded: there the coefficient really is 1, and a
       * student who drops coefficients altogether would be marked correct. */
      const k = rng.int(1, n - 1);
      const answer = choose(n, k);

      return numericExercise({
        prompt: `In the expansion of (x + y)^${n}, what is the coefficient of ${power('x', n - k)}${power('y', k)}?`,
        latex: `(x + y)^{${n}}`,
        value: answer,
        summary: `The term needs a y from exactly ${k} of the ${n} brackets, and there are ${n}C${k} = ${answer} ways to choose which ones.`,
        hint: 'How many of the brackets have to supply a y? In how many ways can you choose them?',
        steps: [
          { label: 'Choose which brackets give the y.', latex: `{}^{${n}}C_{${k}}` },
          { label: 'Evaluate.', latex: `${answer}` },
        ],
        // Every coefficient taken to be 1.
        traps: [...(answer === 1 ? [] : [trap(1, 'binomial-coefficients-omitted')])],
      });
    },
  }),

  template({
    id: 'binomial-named-term',
    conceptId: 'binomial-theorem',
    description: 'Find one particular term of an expansion, counting from zero.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const n = rng.int(6, difficulty === 4 ? 12 : 9);
      const position = rng.int(3, 5);
      const k = position - 1;
      const answer = choose(n, k);
      const offByOne = choose(n, position);

      return numericExercise({
        prompt: `What is the coefficient of the ${position}th term in the expansion of (x + y)^${n}?`,
        latex: `(x + y)^{${n}}`,
        value: answer,
        summary: `The expansion starts at ${n}C0, so the ${position}th term carries ${n}C${k} = ${answer}. Counting from zero is the whole of the difficulty here.`,
        hint: `The first term has index 0. What index does the ${position}th term have?`,
        steps: [
          { label: 'Convert the position to an index.', latex: `k = ${position} - 1 = ${k}` },
          { label: 'Take that coefficient.', latex: `{}^{${n}}C_{${k}} = ${answer}` },
        ],
        // Reading the position straight off as the index.
        traps: [...(offByOne === answer ? [] : [trap(offByOne, 'binomial-term-index-off-by-one')])],
      });
    },
  }),

  template({
    id: 'binomial-expansion-choice',
    conceptId: 'binomial-theorem',
    description: 'Recognise a correct expansion, coefficients and all.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const n = rng.pick([3, 4, 5]);
      const powers = (coefficients: number[]): string =>
        coefficients
          .map((coefficient, k) => {
            const c = coefficient === 1 ? '' : String(coefficient);
            const xPart = n - k === 0 ? '' : n - k === 1 ? 'x' : `x^{${n - k}}`;
            const yPart = k === 0 ? '' : k === 1 ? 'y' : `y^{${k}}`;
            return `${c}${xPart}${yPart}`;
          })
          .join(' + ');

      const correct = powers(Array.from({ length: n + 1 }, (_, k) => choose(n, k)));
      const noCoefficients = powers(Array.from({ length: n + 1 }, () => 1));
      const distributed = `x^{${n}} + y^{${n}}`;
      const reversed = powers(Array.from({ length: n + 1 }, (_, k) => choose(n, k) + 1));

      return choiceExercise({
        prompt: 'Which of these is the correct expansion?',
        latex: `(x + y)^{${n}}`,
        ...options(
          [
            { latex: correct, correct: true },
            { latex: noCoefficients, misconceptionId: 'binomial-coefficients-omitted' },
            { latex: distributed, misconceptionId: 'power-distributed-over-sum' },
            { latex: reversed },
          ],
          rng.shuffle,
        ),
        summary: `The coefficients are row ${n} of Pascal's triangle — ${Array.from({ length: n + 1 }, (_, k) => choose(n, k)).join(', ')} — because each one counts which brackets supplied the y.`,
        hint: 'Put x = y = 1. The true expansion must come to 2 raised to the power.',
      });
    },
  }),

  /* -------------------------------------------------- probability distributions */
  template({
    id: 'distribution-valid',
    conceptId: 'probability-distributions',
    description: 'Check that a distribution’s probabilities total 1.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 4);
      const b = rng.int(1, 4);
      /* c lands in 2..8, so c − 1 below is still a probability. */
      const c = 10 - a - b;

      return choiceExercise({
        prompt: 'A random variable takes the values 1, 2 and 3. Which list of probabilities is a valid distribution?',
        ...options(
          [
            { label: `${a}/10, ${b}/10, ${c}/10`, correct: true },
            { label: `${a}/10, ${b}/10, ${c + 2}/10`, misconceptionId: 'distribution-does-not-total-one' },
            { label: `${a}/10, ${b}/10, ${c - 1}/10`, misconceptionId: 'distribution-does-not-total-one' },
            { label: `${a + 1}/10, ${b + 1}/10, ${c}/10`, misconceptionId: 'distribution-does-not-total-one' },
          ],
          rng.shuffle,
        ),
        summary: `Something must happen, so the probabilities have to total exactly 1: ${a}/10 + ${b}/10 + ${c}/10 = 1.`,
        hint: 'Add each list up. Only one of them can be right.',
      });
    },
  }),

  template({
    id: 'expected-value',
    conceptId: 'probability-distributions',
    description: 'Weight the outcomes by their probabilities.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * The weights are forced apart. On a uniform distribution the unweighted
       * mean *is* the expected value, and the trap below would be the answer.
       */
      const weights = rng.shuffle([1, 2, 7]);
      const values = [rng.int(1, 3), rng.int(4, 7), rng.int(8, 12)];
      const expected = values.reduce((total, value, i) => total + value * (weights[i] as number), 0) / 10;
      const unweighted = values.reduce((total, value) => total + value, 0) / 3;

      return numericExercise({
        prompt: `A prize is worth £${values[0]}, £${values[1]} or £${values[2]}, with probabilities ${weights[0]}/10, ${weights[1]}/10 and ${weights[2]}/10. What is the expected value?`,
        unit: '£',
        value: Math.round(expected * 100) / 100,
        tolerance: within(0.005, expected),
        summary: `Multiply each value by its probability and add: (${values[0]} × ${weights[0]} + ${values[1]} × ${weights[1]} + ${values[2]} × ${weights[2]}) ÷ 10 = ${Math.round(expected * 100) / 100}.`,
        hint: 'The three outcomes are not equally likely. Weight each one by its chance.',
        steps: [
          {
            label: 'Multiply each value by its probability.',
            latex: values.map((value, i) => `${value} \\cdot \\frac{${weights[i]}}{10}`).join(' + '),
          },
          { label: 'Add.', latex: `${Math.round(expected * 100) / 100}` },
        ],
        // The plain average of the outcomes, ignoring how likely each is.
        traps: [
          ...(Math.abs(unweighted - expected) < 0.05
            ? []
            : [trap(Math.round(unweighted * 100) / 100, 'expected-value-unweighted')]),
        ],
      });
    },
  }),

  template({
    id: 'binomial-probability',
    conceptId: 'probability-distributions',
    description: 'Find a binomial probability for a fair coin.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const n = rng.int(4, difficulty === 4 ? 8 : 6);
      const k = rng.int(1, n - 1);
      const ways = choose(n, k);
      const outcomes = 2 ** n;

      return fractionExercise({
        prompt: `A fair coin is tossed ${n} times. What is the probability of exactly ${k} head${k === 1 ? '' : 's'}?`,
        value: fraction(ways, outcomes),
        requireSimplified: true,
        summary: `There are ${ways} ways for exactly ${k} of the ${n} tosses to land heads, out of ${outcomes} equally likely outcomes: ${toText(fraction(ways, outcomes))}.`,
        hint: 'Count the arrangements with that many heads, then divide by all the outcomes.',
        steps: [
          { label: 'Count the favourable patterns.', latex: `{}^{${n}}C_{${k}} = ${ways}` },
          { label: 'Count all the outcomes.', latex: `2^{${n}} = ${outcomes}` },
          { label: 'Divide.', latex: `${toText(fraction(ways, outcomes))}` },
        ],
        // One particular pattern, with the coefficient left out.
        traps: [...(ways === 1 ? [] : [fractionTrap(fraction(1, outcomes), 'binomial-coefficients-omitted')])],
      });
    },
  }),

  template({
    id: 'binomial-formula-choice',
    conceptId: 'probability-distributions',
    description: 'Identify the binomial formula, with n on the outside.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(5, 12);
      const k = rng.int(2, 4);

      return choiceExercise({
        prompt: `In ${n} independent trials, each succeeding with probability p, which expression gives the probability of exactly ${k} successes?`,
        ...options(
          [
            { latex: `{}^{${n}}C_{${k}}\\,p^{${k}}(1-p)^{${n - k}}`, correct: true },
            {
              latex: `{}^{${k}}C_{${n}}\\,p^{${k}}(1-p)^{${n - k}}`,
              misconceptionId: 'binomial-trials-and-successes-swapped',
            },
            { latex: `p^{${k}}(1-p)^{${n - k}}`, misconceptionId: 'binomial-coefficients-omitted' },
            { latex: `{}^{${n}}C_{${k}}\\,p^{${n - k}}(1-p)^{${k}}` },
          ],
          rng.shuffle,
        ),
        summary: `You are choosing which ${k} of the ${n} trials succeed, so the trials are the pool: ${n}C${k}. The remaining ${n - k} trials must fail, which is the (1 − p) factor.`,
        hint: 'Which is the pool you are choosing from — the trials, or the successes?',
      });
    },
  }),
];
